import React, { useCallback, useEffect, useRef, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import RemoveCircleOutlineSharpIcon from '@mui/icons-material/RemoveCircleOutlineSharp';
import { Alert, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTimeoutFn } from 'react-use';
import CutIcon from '@mui/icons-material/ContentCut';
import { TimeSignalClassification } from 'graphql/types';
import { useFormattedDuration } from 'modules/notes/common';
import { useDialogState, useFeatureFlag } from 'modules/common/hooks';
import { useMessages } from 'modules/intl';
import { MAX_ALLOWED_SPLIT } from '../../constants';
import { OFFSET_ENTRY, SplitEntry } from '../../types';
import { useSplitEntriesContext } from '../../SplitEntriesProvider';
import { useDurationSplitBarHoveredEntryContext } from '../DurationSplitBar/DurationSplitBarHoveredEntryProvider';
import { RemoveEntryDialog } from '../RemoveEntryDialog';

type DurationSplitBarProps = {
  classification: TimeSignalClassification;
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'calc(100% - 32px)',
    display: 'flex',
    margin: theme.spacing(3, 2, 1),
    '& div:first-child > div': {
      borderTopLeftRadius: theme.spacing(0.75),
      borderBottomLeftRadius: theme.spacing(0.75)
    },
    '& div:last-child > div': {
      borderTopRightRadius: theme.spacing(0.75),
      borderBottomRightRadius: theme.spacing(0.75)
    }
  },
  maxReachBanner: {
    width: 'calc(100% - 32px)',
    backgroundColor: theme.palette.information.background,
    color: theme.palette.information.dark,
    margin: theme.spacing(2),
    padding: theme.spacing(0.5, 1)
  },
  alert: {
    margin: theme.spacing(2)
  }
}));

export const DurationSplitBar: React.FC<DurationSplitBarProps> = ({
  classification
}: DurationSplitBarProps) => {
  const classes = useStyles();
  const [showAnimation, setShowAnimation] = useState(true);
  useTimeoutFn(() => setShowAnimation(false), 1500);
  const {
    splitEntries,
    splitEntryHandler,
    removeSplitHandler,
    fillOffsetHandler
  } = useSplitEntriesContext();

  const { setHoveredEntryId } = useDurationSplitBarHoveredEntryContext();
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

  return (
    <>
      <div className={classnames(classes.root)} data-testid="note-split-bar">
        {splitEntries.map((entry) => (
          <SplitBar
            key={entry.id}
            entry={entry}
            splitEntryHandler={splitEntryHandler}
            deleteEntryHandler={removeSplitHandler}
            fillOffsetHandler={fillOffsetHandler}
            classification={classification}
            enableSplit={
              splitEntries.length < MAX_ALLOWED_SPLIT &&
              entry.id !== OFFSET_ENTRY
            }
            enableDelete={splitEntries.length > 1 && entry.id !== OFFSET_ENTRY}
            isOverLimit={splitEntries.length > MAX_ALLOWED_SPLIT}
            setHoveredEntryId={setHoveredEntryId}
            showAnimation={showAnimation}
          />
        ))}
      </div>
      {splitEntries.length >= MAX_ALLOWED_SPLIT && (
        <>
          {isTiwScreenReaderEnabled ? (
            <Alert severity="info" className={classes.alert}>
              <Typography variant="caption">
                <FormattedMessage id="note.split.maxReached" />
              </Typography>
            </Alert>
          ) : (
            <div
              className={classes.maxReachBanner}
              data-testid="max_reached_banner"
            >
              <Typography variant="caption">
                <FormattedMessage id="note.split.maxReached" />
              </Typography>
            </div>
          )}
        </>
      )}
    </>
  );
};

type SplitBarProps = {
  entry: SplitEntry;
  splitEntryHandler: (id: string) => void;
  deleteEntryHandler: (id: string) => void;
  fillOffsetHandler: () => void;
  classification: TimeSignalClassification;
  enableSplit: boolean;
  enableDelete: boolean;
  isOverLimit: boolean;
  setHoveredEntryId: (hoveredEntry: string | null) => void;
  showAnimation: boolean;
};

type BarStyleArgs = {
  isOffset: boolean;
  width: number;
  enableNestedIcons: boolean;
};

const useBarStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    height: theme.spacing(4),
    width: '100%',
    borderRadius: theme.spacing(0),
    border: `1px solid ${theme.palette.grey[400]}`,
    background: 'inherit'
  },
  [`${TimeSignalClassification.Unclassified}`]: {
    backgroundColor: ({ isOffset }: BarStyleArgs) =>
      isOffset
        ? theme.palette.notesLabel.inboxHalfOpacity
        : theme.palette.notesLabel.inbox
  },
  [`${TimeSignalClassification.Private}`]: {
    backgroundColor: ({ isOffset }: BarStyleArgs) =>
      isOffset
        ? theme.palette.notesLabel.privateHalfOpacity
        : theme.palette.notesLabel.private
  },
  [`${TimeSignalClassification.Accepted}`]: {
    backgroundColor: ({ isOffset }: BarStyleArgs) =>
      isOffset
        ? theme.palette.notesLabel.acceptedHalfOpacity
        : theme.palette.notesLabel.accepted
  },
  cutIcon: {
    transform: 'rotate(-90deg)',
    color: theme.palette.common.white,
    padding: theme.spacing(0.5)
  },
  cutIconBtn: {
    display: ({ enableNestedIcons }: BarStyleArgs) =>
      enableNestedIcons ? 'inherit' : 'none',
    left: '50%',
    transform: 'translate(-12px)',
    color: theme.palette.common.white,
    width: theme.spacing(3),
    height: theme.spacing(3),
    padding: theme.spacing(0.5),
    background: theme.palette.primary.main,
    '&:hover, &:focus': {
      background: theme.palette.primary.main
    },
    fill: '#ffffff'
  },
  '@keyframes fadeOut': {
    from: {
      opacity: 1
    },
    '30%': {
      opacity: 1
    },
    '50%': {
      opacity: 1
    },
    to: {
      opacity: 0
    }
  },
  showIcon: {
    display: 'inherit',
    animation: '$fadeOut 1.5s'
  },
  deleteIcon: {
    display: ({ enableNestedIcons }: BarStyleArgs) =>
      enableNestedIcons ? 'inherit' : 'none',
    width: theme.spacing(3),
    height: theme.spacing(3),
    marginLeft: 'auto',
    marginRight: theme.spacing(0.5),
    color: theme.palette.secondary.main,
    opacity: 0.5,
    '&:hover, &:focus': {
      opacity: 1
    }
  },
  label: {
    ...theme.typography.caption,
    textAlign: 'center',
    paddingTop: theme.spacing(1.5),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  offsetLabel: {
    opacity: 1,
    color: theme.palette.information.dark,
    marginRight: 'auto',
    marginLeft: 'auto',
    background: theme.palette.information.light,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.25, 1),
    ...theme.typography.caption
  },
  addIcon: {
    position: 'absolute',
    display: ({ enableNestedIcons }: BarStyleArgs) =>
      enableNestedIcons ? 'inherit' : 'none',
    width: theme.spacing(3),
    height: theme.spacing(3),
    color: theme.palette.background.paper,
    background: theme.palette.primary.main,
    '&:hover': {
      background: theme.palette.primary.main
    },
    padding: theme.spacing(1),
    right: theme.spacing(2.5)
  },
  zoom: {
    display: 'inherit',
    width: theme.spacing(11.25),
    '&:hover, &:focus': {
      zIndex: 20
    },
    '&:hover $cutIconBtn, &:focus $cutIconBtn': {
      position: 'relative',
      left: 5,
      boxShadow: '2px 5px 7px 1px rgba(0,0,0,0.5)'
    },
    '&:hover $deleteIcon, &:focus $deleteIcon': {
      position: 'relative',
      right: 6,
      boxShadow: '2px 5px 7px 1px rgba(0,0,0,0.5)'
    }
  },
  actionContainer: {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    //the hover css will be removed with isTiwVpatEnabled removal
    //with enableNestedIcons we can add css for both hover and focus
    '&:hover $cutIconBtn': {
      display: 'inherit'
    },
    '&:hover $deleteIcon': {
      display: 'inherit'
    },
    '&:hover $addIcon': {
      display: 'inherit'
    }
  }
}));

const messageIds = [
  'note.split.split',
  'note.split.remove',
  'note.split.add',
  'note.split.splitEntry',
  'note.split.removeEntry',
  'note.split.addBack'
];

const SplitBar: React.FC<SplitBarProps> = ({
  entry,
  splitEntryHandler,
  deleteEntryHandler,
  fillOffsetHandler,
  classification,
  enableSplit,
  enableDelete,
  isOverLimit,
  setHoveredEntryId,
  showAnimation
}: SplitBarProps) => {
  const onCutClick = useCallback(() => splitEntryHandler(entry.id), [
    entry.id,
    splitEntryHandler
  ]);
  const onDeleteClick = useCallback(() => deleteEntryHandler(entry.id), [
    deleteEntryHandler,
    entry.id
  ]);
  const formattedDuration = useFormattedDuration({
    duration: entry.duration ?? 0,
    needsMask: true
  });

  const [enableNestedIcons, setEnableNestedIcons] = useState(false);

  const isTiwVpatEnabled = useFeatureFlag('isTiwVpatEnabled');
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

  const mouseEnter = useCallback(() => {
    setHoveredEntryId(entry.id);
    if (isTiwVpatEnabled) {
      setEnableNestedIcons(true);
    }
  }, [entry.id, isTiwVpatEnabled, setHoveredEntryId]);

  const mouseLeave = useCallback(() => {
    setHoveredEntryId(null);
    if (isTiwVpatEnabled) {
      setEnableNestedIcons(false);
    }
  }, [isTiwVpatEnabled, setHoveredEntryId]);

  const hideShowClass = useCallback(() => setShowClass(undefined), []);
  useTimeoutFn(hideShowClass, 1500);
  const messages = useMessages(messageIds);

  const barView = useRef<HTMLDivElement | null>(null);
  const [barWidth, setBarWidth] = useState(0);

  useEffect(() => {
    setBarWidth(barView.current?.clientWidth ?? 0);
  }, [barView.current?.clientWidth]);

  const classes = useBarStyles({
    isOffset: entry.id === OFFSET_ENTRY,
    width: barWidth,
    enableNestedIcons
  });
  const [showClass, setShowClass] = useState<string | undefined>(
    classes.showIcon
  );

  const { open, openDialog, closeDialog } = useDialogState();
  const { formatMessage } = useIntl();
  const entryMsg = formatMessage(
    {
      id: 'note.split.enterMsg'
    },
    { entryName: entry.name }
  );

  return (
    <>
      <div style={{ width: `${entry.weight}%` }} ref={barView}>
        <div
          className={classnames(classes.root, classes[classification])}
          data-testid={`split_entry_${entry.id}`}
          onMouseEnter={mouseEnter}
          onMouseLeave={mouseLeave}
          tabIndex={isTiwVpatEnabled ? 0 : -1}
          role={isTiwScreenReaderEnabled ? 'group' : undefined}
          aria-label={isTiwScreenReaderEnabled ? entryMsg : ''}
          onFocus={isTiwVpatEnabled ? mouseEnter : undefined}
        >
          <div
            className={classnames(classes.actionContainer, {
              [classes.zoom]: barWidth < 75
            })}
          >
            {enableSplit && (
              <Tooltip title={messages[messageIds[0]]}>
                <IconButton
                  className={classnames(
                    classes.cutIconBtn,
                    showAnimation && showClass
                  )}
                  onClick={onCutClick}
                  data-testid={`cut_icon_${entry.id}`}
                  size="large"
                  aria-label={
                    isTiwScreenReaderEnabled
                      ? messages['note.split.splitEntry']
                      : ''
                  }
                >
                  <CutIcon className={classes.cutIcon} />
                </IconButton>
              </Tooltip>
            )}
            {enableDelete && (
              <Tooltip title={messages[messageIds[1]]}>
                <IconButton
                  className={classes.deleteIcon}
                  onClick={openDialog}
                  data-testid={`delete_icon_${entry.id}`}
                  size="large"
                  aria-label={
                    isTiwScreenReaderEnabled
                      ? messages['note.split.removeEntry']
                      : ''
                  }
                  onBlur={isTiwVpatEnabled ? mouseLeave : undefined}
                >
                  <RemoveCircleOutlineSharpIcon />
                </IconButton>
              </Tooltip>
            )}
            {entry.id === OFFSET_ENTRY && (
              <>
                <Typography
                  className={classes.offsetLabel}
                  data-testid={'offset_hours'}
                >
                  -{formattedDuration}
                </Typography>
                {!isOverLimit && (
                  <Tooltip title={messages[messageIds[2]]}>
                    <IconButton
                      className={classes.addIcon}
                      onClick={fillOffsetHandler}
                      data-testid="offset_add_icon"
                      size="large"
                      aria-label={
                        isTiwScreenReaderEnabled
                          ? messages['note.split.addBack']
                          : ''
                      }
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </div>
        </div>
        {entry.id !== OFFSET_ENTRY && (
          <Tooltip title={entry.name}>
            <Typography className={classes.label}>{entry.name}</Typography>
          </Tooltip>
        )}
      </div>
      {open && (
        <RemoveEntryDialog
          open={open}
          removeEntry={onDeleteClick}
          closeHandler={closeDialog}
        />
      )}
    </>
  );
};
